import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { computeViewShares } from '@/lib/social-analytics/computeViewShares';
import { formatAnalyticsSyncDate } from '@/lib/social-analytics/formatAnalyticsSyncDate';
import { formatMetric } from '@/lib/social-analytics/formatMetric';
import { platformAccentClass, platformIcon } from './platformVisuals';

type PlatformRow = { platform: string; tracked_content: number; lifetime_views?: number | null };
type AccountRow = {
  id: string;
  platform: string;
  username: string;
  display_name?: string | null;
  tracked_content: number;
  lifetime_views?: number | null;
  latest_complete_sync_at?: string | null;
};
type BreakdownCardProps = {
  platformBreakdowns: readonly PlatformRow[];
  accountBreakdowns: readonly AccountRow[];
  onSelectAccount: (accountId: string) => void;
};

export const BreakdownCard = ({ platformBreakdowns, accountBreakdowns, onSelectAccount }: BreakdownCardProps) => {
  const { t, i18n } = useTranslation();
  const locale = i18n.language.startsWith('pt') ? 'pt-BR' : 'en-US';
  const [view, setView] = useState<'platform' | 'account'>('platform');
  const accounts = computeViewShares(accountBreakdowns);
  const percent = (share: number | null) => (
    share === null ? '—' : new Intl.NumberFormat(locale, { style: 'percent', maximumFractionDigits: 0 }).format(share)
  );
  const empty = (message: string) => <p className="py-10 text-center text-sm text-muted-foreground">{message}</p>;

  return (
    <Card className="border-border/70 shadow-none">
      <CardHeader className="flex-row items-center justify-between gap-3 space-y-0 border-b border-border/60 pb-4">
        <CardTitle className="text-base">{t('analytics.breakdown.title')}</CardTitle>
        <ToggleGroup
          type="single"
          size="sm"
          variant="outline"
          value={view}
          onValueChange={(value) => { if (value === 'platform' || value === 'account') setView(value); }}
          aria-label={t('analytics.breakdown.label')}
        >
          <ToggleGroupItem value="platform" className="h-7 px-2 text-xs">{t('analytics.breakdown.platform')}</ToggleGroupItem>
          <ToggleGroupItem value="account" className="h-7 px-2 text-xs">{t('analytics.breakdown.account')}</ToggleGroupItem>
        </ToggleGroup>
      </CardHeader>
      <CardContent className="p-4 sm:p-5">
        {view === 'platform' ? (
          platformBreakdowns.length === 0 ? empty(t('analytics.platformBreakdown.empty')) : (
            <ul className="space-y-4">
              {platformBreakdowns.map((item) => {
                const Icon = platformIcon(item.platform);
                return (
                  <li key={item.platform} className="flex items-center gap-3">
                    <span className={`h-8 w-1 rounded-full ${platformAccentClass(item.platform)}`} aria-hidden="true" />
                    <Icon className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium">{t(`analytics.platforms.${item.platform}`)}</p>
                      <p className="text-xs text-muted-foreground">{formatMetric(item.tracked_content, locale)} {t('analytics.metrics.trackedContent').toLowerCase()}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-semibold tabular-nums">{formatMetric(item.lifetime_views ?? null, locale)}</p>
                      <p className="text-[11px] text-muted-foreground">{t('analytics.metrics.lifetimeViews')}</p>
                    </div>
                  </li>
                );
              })}
            </ul>
          )
        ) : accounts.length === 0 ? empty(t('analytics.breakdown.emptyAccounts')) : (
          <ul className="space-y-1" aria-label={t('analytics.breakdown.account')}>
            {accounts.map((account) => {
              const Icon = platformIcon(account.platform);
              const lastSync = formatAnalyticsSyncDate(account.latest_complete_sync_at, locale, t('analytics.status.noSuccessfulSync'));
              return (
                <li key={account.id}>
                  <button
                    type="button"
                    onClick={() => onSelectAccount(account.id)}
                    title={t('analytics.breakdown.lastSync', { date: lastSync })}
                    className="flex w-full items-center gap-3 rounded-lg px-2 py-2 text-left hover:bg-muted/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  >
                    <Icon className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium">@{account.username}</p>
                      <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-muted" aria-hidden="true">
                        <div className={`h-full rounded-full ${platformAccentClass(account.platform)}`} style={{ width: `${Math.round((account.share ?? 0) * 100)}%` }} />
                      </div>
                      <p className="mt-1 text-xs text-muted-foreground">{t('analytics.breakdown.posts', { count: account.tracked_content })}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-semibold tabular-nums">{formatMetric(account.lifetime_views ?? null, locale)}</p>
                      <p className="text-[11px] text-muted-foreground">{t('analytics.breakdown.shareOfViews', { value: percent(account.share) })}</p>
                    </div>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </CardContent>
    </Card>
  );
};
